import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Save, Upload, LogOut, X, ImageIcon } from "lucide-react";
import { adminApi, clearToken, getToken, resolveAssetUrl } from "@/lib/api";

const EMPTY_CASE = {
  number: "",
  title: "",
  brand: "",
  role: "",
  period: "",
  description: "",
  results: [],
  tags: [],
  link: "",
  cover: "",
  order: 0,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(EMPTY_CASE);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [navigate]);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await adminApi.get("/cases");
      setCases(data);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin/login");
      else toast.error("Fehler beim Laden der Case Studies");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearToken();
    toast.success("Abgemeldet");
    navigate("/admin/login");
  };

  const reset = () => {
    setDraft(EMPTY_CASE);
    setEditingId(null);
  };

  const onEdit = (c) => {
    setEditingId(c.id);
    setDraft({
      number: c.number || "",
      title: c.title || "",
      brand: c.brand || "",
      role: c.role || "",
      period: c.period || "",
      description: c.description || "",
      results: c.results || [],
      tags: c.tags || [],
      link: c.link || "",
      cover: c.cover || "",
      order: c.order || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onDelete = async (id) => {
    if (!window.confirm("Diese Case Study wirklich löschen?")) return;
    try {
      await adminApi.delete(`/admin/cases/${id}`);
      toast.success("Case Study gelöscht");
      if (editingId === id) reset();
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!draft.title.trim()) {
      toast.error("Titel ist erforderlich");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...draft,
        results: draft.results.filter((r) => r.trim()),
        tags: draft.tags.filter((t) => t.trim()),
        link: draft.link?.trim() || null,
      };
      if (editingId) {
        await adminApi.put(`/admin/cases/${editingId}`, payload);
        toast.success("Case Study aktualisiert");
      } else {
        await adminApi.post("/admin/cases", payload);
        toast.success("Case Study erstellt");
      }
      reset();
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Speichern fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 6 MB)");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await adminApi.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDraft((d) => ({ ...d, cover: data.url }));
      toast.success("Bild hochgeladen");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const updateField = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const updateList = (k) => (e) =>
    setDraft((d) => ({ ...d, [k]: e.target.value.split("\n") }));

  return (
    <main data-testid="admin-dashboard-page" className="min-h-screen bg-[#F4F4F0]">
      <header className="border-b-2 border-black bg-white sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-display uppercase text-lg">
              SDA<span className="text-[#FF3B00]">.</span>
            </Link>
            <span className="font-mono-acc text-[10px] text-[#525252]">/ admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-view-site-btn"
              className="hidden sm:inline-block font-mono-acc text-[10px] px-3 py-2 border-2 border-black hover:bg-black hover:text-white transition"
            >
              Seite ansehen
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 font-mono-acc text-[10px] px-3 py-2 bg-black text-white border-2 border-black hover:bg-[#FF3B00] hover:border-[#FF3B00] transition"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Editor */}
          <div className="lg:col-span-5">
            <p className="font-mono-acc text-[11px] text-[#525252] mb-2">// {editingId ? "Bearbeiten" : "Neu anlegen"}</p>
            <h1 className="font-display uppercase text-4xl md:text-5xl leading-[0.9] mb-6">
              {editingId ? "Case bearbeiten" : "Neue Case Study"}
            </h1>

            <form
              onSubmit={onSave}
              data-testid="case-editor-form"
              className="bg-white border-2 border-black shadow-brutal p-5 md:p-7 space-y-5"
            >
              {/* Cover upload */}
              <div>
                <label className="font-mono-acc text-[11px] block mb-2">Cover-Bild</label>
                <div className="flex items-stretch gap-3">
                  <div className="w-28 h-28 border-2 border-black bg-[#F4F4F0] flex items-center justify-center overflow-hidden">
                    {draft.cover ? (
                      <img
                        src={resolveAssetUrl(draft.cover)}
                        alt="Cover"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-[#525252]" />
                    )}
                  </div>
                  <div className="flex-1 flex flex-col gap-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      onChange={onUpload}
                      data-testid="case-upload-input"
                      className="hidden"
                    />
                    <button
                      type="button"
                      data-testid="case-upload-btn"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-black text-white border-2 border-black hover:bg-[#FF3B00] hover:border-[#FF3B00] font-mono-acc text-[10px] disabled:opacity-60"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {uploading ? "Lädt…" : draft.cover ? "Bild ersetzen" : "Bild hochladen"}
                    </button>
                    <input
                      type="text"
                      value={draft.cover}
                      onChange={updateField("cover")}
                      placeholder="…oder Bild-URL einfügen"
                      data-testid="case-cover-url-input"
                      className="w-full px-3 py-2 bg-[#F4F4F0] border-2 border-black focus:outline-none text-xs"
                    />
                  </div>
                </div>
              </div>

              <FieldRow>
                <Field label="Nummer (z.B. 01)" testid="case-number">
                  <input value={draft.number} onChange={updateField("number")} className={inputCls} />
                </Field>
                <Field label="Reihenfolge" testid="case-order">
                  <input
                    type="number"
                    value={draft.order}
                    onChange={(e) => setDraft((d) => ({ ...d, order: parseInt(e.target.value || "0", 10) }))}
                    className={inputCls}
                  />
                </Field>
              </FieldRow>

              <Field label="Titel *" testid="case-title">
                <input value={draft.title} onChange={updateField("title")} className={inputCls} required />
              </Field>

              <FieldRow>
                <Field label="Marke / Kunde" testid="case-brand">
                  <input value={draft.brand} onChange={updateField("brand")} className={inputCls} />
                </Field>
                <Field label="Rolle" testid="case-role">
                  <input value={draft.role} onChange={updateField("role")} className={inputCls} />
                </Field>
              </FieldRow>

              <FieldRow>
                <Field label="Zeitraum" testid="case-period">
                  <input value={draft.period} onChange={updateField("period")} className={inputCls} placeholder="2024 — 2025" />
                </Field>
                <Field label="Link (optional)" testid="case-link">
                  <input value={draft.link} onChange={updateField("link")} className={inputCls} placeholder="https://…" />
                </Field>
              </FieldRow>

              <Field label="Beschreibung" testid="case-description">
                <textarea
                  rows={4}
                  value={draft.description}
                  onChange={updateField("description")}
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <Field label="Key Results (eine pro Zeile)" testid="case-results">
                <textarea
                  rows={3}
                  value={(draft.results || []).join("\n")}
                  onChange={updateList("results")}
                  placeholder={"+340% Reichweite\n12 virale Posts"}
                  className={`${inputCls} resize-none font-mono-acc text-[12px]`}
                />
              </Field>

              <Field label="Tags (Komma-getrennt)" testid="case-tags">
                <input
                  value={(draft.tags || []).join(", ")}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      tags: e.target.value.split(",").map((s) => s.trim()),
                    }))
                  }
                  placeholder="TikTok, Performance"
                  className={inputCls}
                />
              </Field>

              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                {editingId ? (
                  <button
                    type="button"
                    data-testid="case-cancel-edit-btn"
                    onClick={reset}
                    className="font-mono-acc text-[10px] px-3 py-2 border-2 border-black hover:bg-black hover:text-white inline-flex items-center gap-2"
                  >
                    <X className="w-3.5 h-3.5" /> Abbrechen
                  </button>
                ) : (
                  <span className="font-mono-acc text-[10px] text-[#525252]">
                    Pflichtfeld: Titel
                  </span>
                )}
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="case-save-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF3B00] text-white border-2 border-black shadow-brutal hover:translate-y-[2px] hover:shadow-brutal-press font-mono-acc text-[10px] disabled:opacity-60"
                >
                  <Save className="w-3.5 h-3.5" />
                  {saving ? "Speichert…" : editingId ? "Aktualisieren" : "Anlegen"}
                </button>
              </div>
            </form>
          </div>

          {/* List */}
          <div className="lg:col-span-7">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="font-mono-acc text-[11px] text-[#525252] mb-1">// Vorhandene Cases</p>
                <h2 className="font-display uppercase text-3xl md:text-4xl">
                  {cases.length} <span className="text-[#FF3B00]">Cases</span>
                </h2>
              </div>
              <button
                type="button"
                data-testid="case-new-btn"
                onClick={reset}
                className="inline-flex items-center gap-2 px-3 py-2 bg-black text-white border-2 border-black font-mono-acc text-[10px] hover:bg-[#FF3B00] hover:border-[#FF3B00]"
              >
                <Plus className="w-3.5 h-3.5" /> Neu
              </button>
            </div>

            {loading ? (
              <p className="font-mono-acc text-xs text-[#525252]">Lädt…</p>
            ) : cases.length === 0 ? (
              <div className="border-2 border-dashed border-black p-10 text-center">
                <p className="font-display uppercase text-2xl mb-2">Noch leer.</p>
                <p className="font-mono-acc text-[11px] text-[#525252]">
                  Lege links deine erste Case Study an — die Seite zeigt sie sofort öffentlich.
                </p>
              </div>
            ) : (
              <ul data-testid="case-list" className="space-y-4">
                {cases.map((c) => (
                  <li
                    key={c.id}
                    data-testid={`case-row-${c.id}`}
                    className="bg-white border-2 border-black p-4 md:p-5 flex flex-wrap items-center gap-4"
                  >
                    <div className="w-20 h-20 border-2 border-black bg-[#F4F4F0] overflow-hidden shrink-0">
                      {c.cover ? (
                        <img src={resolveAssetUrl(c.cover)} alt={c.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-[#525252]" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <p className="font-mono-acc text-[10px] text-[#525252]">
                        {c.number ? `Case ${c.number} · ` : ""}{c.period || "—"}
                      </p>
                      <h3 className="font-display uppercase text-lg md:text-xl mt-1">{c.title}</h3>
                      <p className="font-mono-acc text-[10px] text-[#FF3B00] mt-0.5">{c.brand || "—"}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        data-testid={`case-edit-${c.id}`}
                        onClick={() => onEdit(c)}
                        className="font-mono-acc text-[10px] px-3 py-2 border-2 border-black hover:bg-black hover:text-white"
                      >
                        Bearbeiten
                      </button>
                      <button
                        data-testid={`case-delete-${c.id}`}
                        onClick={() => onDelete(c.id)}
                        className="font-mono-acc text-[10px] px-3 py-2 border-2 border-black hover:bg-[#FF3B00] hover:border-[#FF3B00] hover:text-white inline-flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-[#F4F4F0] border-2 border-black focus:outline-none focus:bg-[#FFF6F2] text-sm";

const FieldRow = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
);

const Field = ({ label, testid, children }) => (
  <div>
    <label className="font-mono-acc text-[10px] block mb-1.5">{label}</label>
    <div data-testid={`${testid}-wrap`}>{children}</div>
  </div>
);
